import {DOCUMENT, NgIf} from "@angular/common";
import {
    afterNextRender,
    Component,
    ElementRef,
    inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild
} from "@angular/core";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {environment} from "../../../../../environments/environment";
import {AuthService} from "../../../../core/auth/auth.service";
import {PageConfigurationService} from '../../../../core/services/page-configuration.service';
import {PageConfigurationResponse} from '../../../../core/types/page-configuration.types';
import {HttpErrorResponse} from "@angular/common/http";
import {ERROR_CODES} from "../../../../app.constants";
import {FormMaxLengths} from "../../../../core/validators/form-max-lengths";
import {strictEmailValidator} from "../../../../core/validators/strict-email.validator";

import {FormValueSyncDirective} from "../../../../core/directives/form-value-sync.directive";
import {FormValueSyncRegistry} from "../../../../core/directives/form-value-sync.registry";

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_CALLBACK = 'onloadTurnstileCallback';

declare global {
    interface Window {
        [TURNSTILE_CALLBACK]?: () => void;
        turnstile?: {
            render: (el: HTMLElement, options: Record<string, unknown>) => string;
            execute: (widgetId: string) => void;
            reset: (widgetId: string) => void;
            remove: (widgetId: string) => void;
        };
    }
}

@Component({
    selector: "app-login",
    standalone: true,
    imports: [NgIf, ReactiveFormsModule, FormsModule, FormValueSyncDirective],
    templateUrl: "./login.component.html",
    styleUrl: "./login.component.scss",
})
export class LoginComponent implements OnInit, OnDestroy {
    private _formValueSync = inject(FormValueSyncRegistry);
    @Input() onAuthSuccess?: () => void;
    @ViewChild('turnstileContainer', { static: false }) turnstileContainer?: ElementRef<HTMLElement>;

    errorMessage: string = '';
    loginForm!: FormGroup;
    turnstileSiteKey: string = environment.turnstileSettings.siteKey;
    isLoading: boolean = false;

    maskedEmail: string = '';
    maskedPhone: string = '';

    readonly maxEmailLength = FormMaxLengths.Email;
    readonly maxPhoneLength = FormMaxLengths.Phone;

    private _captchaToken: string = '';
    private _widgetId?: string;
    private _pendingSubmit: boolean = false;

    private _authService = inject(AuthService);
    private _route = inject(ActivatedRoute);
    private _configurationService = inject(PageConfigurationService);
    private _zone = inject(NgZone);
    private _document = inject(DOCUMENT);

    constructor() {
        afterNextRender(() => this.loadAndRenderTurnstile());
    }

    ngOnInit(): void {
        this.loginForm = new FormGroup({
            email: new FormControl(null, [strictEmailValidator(), Validators.maxLength(FormMaxLengths.Email)]),
            phoneNumber: new FormControl(null, [Validators.maxLength(FormMaxLengths.Phone)]),
        });

        this._configurationService.pageConfiguration$.subscribe(
            (response: PageConfigurationResponse | null) => {
                if (response && response.success) {
                    this.maskedEmail = response.data!.loginHint.emailMasked;
                    this.maskedPhone = response.data!.loginHint.phoneMasked;
                }
            }
        );
    }

    ngOnDestroy(): void {
        if (this._widgetId && window.turnstile) {
            window.turnstile.remove(this._widgetId);
        }
    }

    get loginFormControls() {
        return {
            email: this.loginForm.get("email") as FormControl,
            phoneNumber: this.loginForm.get("phoneNumber") as FormControl
        }
    }

    onSubmit(): void {
        if (this.isLoading) {
            return;
        }

        this._formValueSync.syncAll();

        this.errorMessage = '';

        if (this.loginForm.invalid) {
            this.setErrorMessage('Please fill in the required fields correctly.');
            this.loginForm.markAllAsTouched();
            return;
        }

        const email = this.loginFormControls.email.value;
        const phone = this.loginFormControls.phoneNumber.value;

        if (!email && !phone) {
            this.setErrorMessage('Please enter either your email address or phone number.');
            return;
        }

        this.isLoading = true;

        if (this._captchaToken) {
            this.completeSignIn();
            return;
        }

        if (!this._widgetId || !window.turnstile) {
            this.isLoading = false;
            this.setErrorMessage('CAPTCHA is still loading. Please try again in a moment.');
            return;
        }

        this._pendingSubmit = true;
        window.turnstile.execute(this._widgetId);
    }

    private completeSignIn(): void {
        const phoneNumber = this.loginFormControls.phoneNumber.value
            ? this.loginFormControls.phoneNumber.value.replace(/\D/g, '')
            : null;

        const credentials = {
            email: this.loginFormControls.email.value || null,
            phoneNumber: phoneNumber || null,
            captchaToken: this._captchaToken,
            shortLink: this._route.snapshot.paramMap.get('shortLink') || ''
        };

        this._authService.signIn(credentials).subscribe({
            next: () => {
                this.isLoading = false;

                if (this.onAuthSuccess) {
                    this.onAuthSuccess();
                }
            },
            error: (errorResponse: HttpErrorResponse) => {
                this.isLoading = false;
                this.resetCaptcha();
                this.setErrorMessage(errorResponse.error.detail === ERROR_CODES.TENANT_DISABLED
                    ? errorResponse.error.title
                    : 'The email or phone number you entered does not match our records. Please try again.');
            }
        });
    }

    private loadAndRenderTurnstile(): void {
        if (window.turnstile) {
            this.renderWidget();
            return;
        }

        if (this._document.getElementById(TURNSTILE_SCRIPT_ID)) {
            const existing = window[TURNSTILE_CALLBACK];
            window[TURNSTILE_CALLBACK] = () => {
                existing?.();
                this.renderWidget();
            };
            return;
        }

        window[TURNSTILE_CALLBACK] = () => this.renderWidget();

        const script = this._document.createElement('script');
        script.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${TURNSTILE_CALLBACK}`;
        script.id = TURNSTILE_SCRIPT_ID;
        script.async = true;
        script.defer = true;
        this._document.head.appendChild(script);
    }

    private renderWidget(): void {
        if (!this.turnstileContainer?.nativeElement || !window.turnstile) {
            return;
        }

        this._widgetId = window.turnstile.render(this.turnstileContainer.nativeElement, {
            sitekey: this.turnstileSiteKey,
            theme: 'auto',
            execution: 'execute',
            appearance: 'interaction-only',
            callback: (token: string) => {
                this._zone.run(() => {
                    this._captchaToken = token;
                    if (this._pendingSubmit) {
                        this._pendingSubmit = false;
                        this.completeSignIn();
                    }
                });
            },
            'error-callback': () => {
                this._zone.run(() => {
                    this._captchaToken = '';
                    this._pendingSubmit = false;
                    this.isLoading = false;
                    this.setErrorMessage('CAPTCHA validation failed. Please try again.');
                });
                return false;
            },
            'expired-callback': () => {
                this._zone.run(() => {
                    this._captchaToken = '';
                });
            },
        });
    }

    private resetCaptcha(): void {
        if (this._widgetId && window.turnstile) {
            window.turnstile.reset(this._widgetId);
        }
        this._captchaToken = '';
        this._pendingSubmit = false;
    }

    private setErrorMessage(message: string): void {
        this.errorMessage = message;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
}
